import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ApropriacaoEntrada } from '@triade/contracts';
import {
  useAtualizarConfiguracaoRateio,
  useConfiguracaoRateio,
  useCriarConfiguracaoRateio,
  useDestinosConfiguracao,
  useEncerrarConfiguracaoRateio,
} from '@/api/configuracoes-rateio';
import { ApropriacaoPorCentro, type ErroApropriacao } from '@/components/apropriacao';
import { ErroLista } from '@/components/cargos';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { apropriacaoParaEntrada } from '@/components/imobilizados/imobilizado-form';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

export interface BuscaConfiguracoes {
  id?: string;
}

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/** Codigos que a apropriacao (destinos e somas) explica: vao tambem para o bloco da apropriacao. */
const CODIGOS_DA_APROPRIACAO = ['DESTINO_RATEIO_INVALIDO'];

/** Configuracao de rateio de um centro de apoio (`?id=`; `novo` cria). Sem `rateio.editar`, so leitura. */
export function ConfiguracaoDetalhe({ id, busca: _busca, podeEditar }: { id: string | undefined; busca: BuscaConfiguracoes; podeEditar: boolean }) {
  const navigate = useNavigate();
  const doc = useConfiguracaoRateio(id);
  const criar = useCriarConfiguracaoRateio();
  const atualizar = useAtualizarConfiguracaoRateio();
  const encerrar = useEncerrarConfiguracaoRateio();
  const config = doc.data;

  const [centroNovo, setCentroNovo] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [criadoId, setCriadoId] = useState<string | null>(null);
  /** Apropriacao editada na tela (null = nada editado; vale a gravada). `completa` vem do componente. */
  const [apropriacao, setApropriacao] = useState<{ valor: ApropriacaoEntrada | null; completa: boolean } | null>(null);
  const apropriacaoAlterada = apropriacao !== null;
  const [chaveApropriacao, setChaveApropriacao] = useState(0);
  const [erroApropriacao, setErroApropriacao] = useState<ErroApropriacao | null>(null);
  const apropriacaoGravada = useMemo(() => apropriacaoParaEntrada(config?.apropriacao ?? null), [config]);

  /** Novo: destinos do centro escolhido (ou da proxima ordem). Existente: destinos da propria configuracao. */
  const destinos = useDestinosConfiguracao(id ? { centroId: config?.centroId, ordem: config?.ordem } : { centroId: centroNovo || undefined });
  const centrosPermitidos = destinos.data?.destinos.map((d) => d.id) ?? [];

  const dirty = (!id && centroNovo !== '') || validFrom !== '' || apropriacaoAlterada;
  const alterados = (!id && centroNovo !== '' ? 1 : 0) + (validFrom !== '' ? 1 : 0) + (apropriacaoAlterada ? 1 : 0);
  const bloqueio = useBlockerDirty(dirty && criadoId === null);
  const salvando = criar.isPending || atualizar.isPending;

  useEffect(() => {
    if (criadoId) void navigate({ to: '/custos/configuracoes-rateio', search: { id: criadoId }, replace: true });
  }, [criadoId, navigate]);

  if (id && doc.isError) return <section className="rounded-2xl border border-border bg-card p-5"><ErroLista erro={doc.error} onTentar={() => void doc.refetch()} titulo="Não foi possível abrir a configuração de rateio" /></section>;
  if (id && !config) return <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-muted" role="status">Carregando configuração de rateio…</section>;

  const lista = { to: '/custos/configuracoes-rateio' as const, search: {} };

  function limparRascunho() {
    setCentroNovo('');
    setValidFrom('');
    setApropriacao(null);
    setChaveApropriacao((k) => k + 1);
  }

  async function gravar() {
    setErroSalvar(null);
    setErroApropriacao(null);
    if (!id && centroNovo === '') {
      setErroSalvar('Escolha o centro de apoio');
      return;
    }
    // MANUAL precisa fechar 100% (e ter linhas completas): editada e incompleta nao vai para a API.
    if (apropriacao && !apropriacao.completa) {
      setErroApropriacao({ code: 'APROPRIACAO_INCOMPLETA', message: 'A apropriação precisa fechar 100% (e ter todas as linhas completas) para salvar.' });
      return;
    }
    const entrada = apropriacao ? apropriacao.valor : apropriacaoGravada;
    if (entrada === null) {
      setErroApropriacao({ code: 'APROPRIACAO_INCOMPLETA', message: 'Informe a apropriação antes de salvar.' });
      return;
    }
    try {
      if (id) {
        await atualizar.mutateAsync({ id, apropriacao: entrada, ...(validFrom ? { validFrom } : {}) });
        setApropriacao(null);
        setValidFrom('');
        setChaveApropriacao((k) => k + 1);
        setSalvoEm(new Date());
      } else {
        const criada = await criar.mutateAsync({ centroId: centroNovo, apropriacao: entrada, ...(validFrom ? { validFrom } : {}) });
        limparRascunho();
        setSalvoEm(new Date());
        setCriadoId(criada.id);
      }
    } catch (e) {
      setErroSalvar(mensagemApi(e));
      if (e instanceof ApiError) {
        const issueApropriacao = e.body?.issues?.find((i) => i.path.startsWith('apropriacao'));
        if (issueApropriacao || e.code?.startsWith('APROPRIACAO') || e.code?.startsWith('SOMA') || (e.code && CODIGOS_DA_APROPRIACAO.includes(e.code))) {
          setErroApropriacao({ code: e.code ?? 'APROPRIACAO', message: issueApropriacao?.message ?? e.message });
        }
      }
    }
  }

  async function encerrarConfiguracao() {
    if (!id) return;
    setErroSalvar(null);
    try {
      await encerrar.mutateAsync(id);
      void navigate({ ...lista, replace: true });
    } catch (e) {
      setErroSalvar(mensagemApi(e));
    }
  }

  const rotuloCentro = config ? `${config.centroCodigo} — ${config.centroNome}` : '';

  return (
    <>
      <section aria-label="Dados da configuração de rateio" className="rounded-2xl border border-border bg-card p-5">
        <h2 className="text-body font-semibold">{config ? `${config.ordem}ª — ${config.centroNome}` : 'Nova configuração de rateio'}</h2>
        {!podeEditar && (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Somente leitura.</b> Seu acesso permite consultar as configurações de rateio, não alterá-las.
          </p>
        )}

        <fieldset disabled={!podeEditar} className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
          {id ? (
            <div>
              <div className={ROTULO}>Centro de apoio</div>
              <div className="flex h-[34px] items-center text-small text-ink-body" data-testid="centro-configuracao">{rotuloCentro}</div>
            </div>
          ) : (
            <div>
              <label htmlFor="cr-centro" className={ROTULO}>Centro de apoio</label>
              <select id="cr-centro" className={CAMPO} value={centroNovo} onChange={(e) => { setCentroNovo(e.target.value); setApropriacao(null); setChaveApropriacao((k) => k + 1); }}>
                <option value="">Escolha um centro</option>
                {(destinos.data?.apoiosSemConfiguracao ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.codigo} — {c.nome}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="cr-vigencia" className={ROTULO}>Início da vigência</label>
            <input id="cr-vigencia" type="date" className={CAMPO} value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            {id && <p className="mt-1 text-caption text-ink-muted">Em branco corrige a configuração atual; data futura abre nova vigência.</p>}
          </div>
        </fieldset>

        <div className="mt-5">
          <h4 className="mb-2 text-small font-semibold">Apropriação do centro de apoio</h4>
          <p className="mb-3 text-caption text-ink-muted">Um centro de apoio só rateia para centros de transformação e para apoios de ordem posterior; nunca para si nem para os anteriores.</p>
          <div className={cn('mt-3', !podeEditar && 'opacity-80')}>
            <ApropriacaoPorCentro
              key={`${id ?? 'novo'}-${chaveApropriacao}`}
              value={apropriacaoGravada}
              onChange={(valor, situacao) => setApropriacao({ valor, completa: situacao?.completa ?? valor !== null })}
              resolvida={config?.apropriacao ?? null}
              referTo="CENTRO_CUSTO"
              centrosPermitidos={centrosPermitidos}
              disabled={!podeEditar || salvando || (!id && centroNovo === '')}
              erro={erroApropriacao}
            />
          </div>
        </div>
      </section>

      {podeEditar && (
        <RecordToolbar
          dirty={dirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erroSalvar}
          novo={!id}
          canPrev={false}
          canNext={false}
          alteradosCount={dirty ? alterados : undefined}
          onList={() => void navigate({ ...lista, replace: true })}
          onSave={() => void gravar()}
          onNew={() => void navigate({ to: '/custos/configuracoes-rateio', search: { id: 'novo' } })}
          onDelete={id ? () => void encerrarConfiguracao() : undefined}
          excluirTitulo="Encerrar configuração"
          onUndo={() => {
            setErroSalvar(null);
            setErroApropriacao(null);
            limparRascunho();
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}
